/**
 * tga-page-modal
 *
 * Generic "open another page in a modal overlay" trigger. Any element with
 * `data-tga-modal-trigger` becomes a click target. On click, the modal
 * fetches the element's `href` (or `data-tga-modal-url`), extracts the
 * Shopify section bodies from the response, and renders them inline.
 *
 * Markup example:
 *   <a href="/pages/how-to-measure" data-tga-modal-trigger>
 *     How to take your measurements
 *   </a>
 *
 * Optional attributes on the trigger:
 *   data-tga-modal-url    Override the URL (defaults to href)
 *   data-tga-modal-label  Override the dialog's accessible label
 *                         (defaults to the trigger's text content)
 *
 * Falls back gracefully: if JS is disabled or fails, the link works as a
 * normal link to the target page.
 */
(function () {
  'use strict';

  var contentCache = {};

  function init() {
    var triggers = document.querySelectorAll(
      '[data-tga-modal-trigger]:not([data-tga-modal-bound])'
    );
    triggers.forEach(function (el) {
      el.setAttribute('data-tga-modal-bound', '1');
      el.addEventListener('click', onTriggerClick);
    });
  }

  function onTriggerClick(e) {
    var el = e.currentTarget;
    var url = el.getAttribute('data-tga-modal-url') || el.getAttribute('href');
    if (!url) return;

    // Let cmd/ctrl/middle-click open in a new tab as a normal link.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    e.preventDefault();
    var label = el.getAttribute('data-tga-modal-label') || el.textContent.trim();
    openModal(url, label);
  }

  function openModal(url, label) {
    if (document.querySelector('.tga-modal-overlay')) return;

    var overlay = document.createElement('div');
    overlay.className = 'tga-modal-overlay';
    overlay.innerHTML =
      '<div class="tga-modal-dialog" role="dialog" aria-modal="true">' +
        '<button type="button" class="tga-modal-close" aria-label="Close">&times;</button>' +
        '<button type="button" class="tga-modal-newtab" aria-label="Open in new tab">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>' +
            '<path d="M15 3h6v6"/>' +
            '<path d="m10 14 11-11"/>' +
          '</svg>' +
        '</button>' +
        '<div class="tga-modal-body"><div class="tga-modal-loading">Loading…</div></div>' +
      '</div>';
    overlay.querySelector('.tga-modal-dialog').setAttribute('aria-label', label);
    document.body.appendChild(overlay);

    requestAnimationFrame(function () {
      overlay.classList.add('tga-modal-visible');
    });
    document.body.classList.add('tga-modal-no-scroll');

    var closeBtn = overlay.querySelector('.tga-modal-close');
    closeBtn.addEventListener('click', function () { closeModal(overlay); });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal(overlay);
    });

    var newTabBtn = overlay.querySelector('.tga-modal-newtab');
    newTabBtn.addEventListener('click', function () {
      window.open(url, '_blank', 'noopener,noreferrer');
      closeModal(overlay);
    });

    var onEsc = function (e) {
      if (e.key === 'Escape') {
        closeModal(overlay);
        document.removeEventListener('keydown', onEsc);
      }
    };
    document.addEventListener('keydown', onEsc);

    setTimeout(function () { closeBtn.focus(); }, 100);

    var body = overlay.querySelector('.tga-modal-body');

    if (contentCache[url]) {
      body.innerHTML = contentCache[url];
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var sections = doc.querySelectorAll(
          'main .shopify-section[id^="shopify-section-template"]'
        );
        var content = '';
        sections.forEach(function (s) { content += s.innerHTML; });
        contentCache[url] = content || (
          '<p>Could not load content. ' +
          '<a href="' + url + '">Open page</a> instead.</p>'
        );
        body.innerHTML = contentCache[url];
      })
      .catch(function () {
        body.innerHTML =
          '<p>Could not load. ' +
          '<a href="' + url + '">Open page</a> instead.</p>';
      });
  }

  function closeModal(overlay) {
    overlay.classList.remove('tga-modal-visible');
    document.body.classList.remove('tga-modal-no-scroll');
    setTimeout(function () { overlay.remove(); }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
